/**
 * Gemini Service - Wrapper for AI generation (now routes through User Backend)
 * 
 * This service maintains backward compatibility with existing components
 * while routing all AI requests through the secure User Backend.
 */

import * as aiService from './aiService';
import { fetchImageViaProxy, uploadDataUriToBackend, isAlreadyPersisted } from './backendService';

// Helper to convert CDN URL → base64 → GCS URL.
// CDN URLs (fal.media) expire and cause CORS issues, so we persist them to our GCS
// bucket and store the GCS URL in the project state instead of giant base64 strings.
//
// Apr 2026 migration: the admin backend now persists fal.ai → GCS server-side, so
// image URLs returned from generation jobs are typically already on storage.googleapis.com.
// In that fast path we short-circuit and skip the proxy fetch + re-upload entirely.
async function persistGeneratedImage(imageUrl) {
    if (!imageUrl) return null;

    // Fast path: server already persisted to GCS — just use the URL as-is
    if (isAlreadyPersisted(imageUrl) || (typeof imageUrl === 'string' && imageUrl.includes('/api/proxy/'))) {
        return imageUrl;
    }

    let base64 = null;

    if (imageUrl.startsWith('data:')) {
        base64 = imageUrl;
    } else if (imageUrl.startsWith('http')) {
        try {
            console.log('🔄 Converting generated image to base64:', imageUrl.substring(0, 60) + '...');
            base64 = await fetchImageViaProxy(imageUrl);
            if (!base64 || !base64.startsWith('data:')) {
                console.warn('⚠️ Proxy returned no base64; keeping original URL');
                return imageUrl;
            }
            console.log('✅ Successfully converted to base64');
        } catch (e) {
            console.warn('⚠️ Failed to convert to base64, keeping URL:', e.message);
            return imageUrl;
        }
    }

    if (!base64) return imageUrl;

    // Upload base64 to GCS via user backend so we persist a clean, durable URL
    try {
        const userEmail = sessionStorage.getItem('userEmail');
        if (!userEmail) {
            console.warn('⚠️ No userEmail in sessionStorage; storing as base64 (project bloat warning)');
            return base64;
        }
        const gcsUrl = await uploadDataUriToBackend(base64, userEmail);
        if (gcsUrl && typeof gcsUrl === 'string' && gcsUrl.startsWith('http')) {
            console.log('☁️ Persisted to GCS:', gcsUrl.substring(0, 80) + '...');
            return gcsUrl;
        }
        console.warn('⚠️ GCS upload returned no URL; storing as base64');
        return base64;
    } catch (e) {
        console.warn('⚠️ GCS persist failed; storing as base64:', e.message);
        return base64;
    }
}

// Backward-compatible alias (older callers expect this name)
const convertToBase64IfNeeded = persistGeneratedImage;

// ==================== SCRIPT GENERATION ====================

export const generateScriptFromAudio = async (audioData, style = 'dialogue', region = 'Global', language = 'English', projectDetails = {}) => {
    // For now, route through the AI service or handle locally
    // This requires audio transcription + script generation
    try {
        const transcription = await aiService.transcribeAudio(audioData);
        return await aiService.generateScript(transcription, style, region, language, projectDetails);
    } catch (e) {
        console.error('Script from audio generation failed:', e);
        throw e;
    }
};

export const generateScriptFromConcept = async (concept, style, region, language, projectDetails = {}) => {
    return aiService.generateScript(concept, style, region, language, projectDetails);
};

export const generateScriptFromVideo = async (videoInput, instructions, style = 'dialogue', region = 'Global', language = 'English', projectDetails = {}) => {
    // This would require video analysis - placeholder for now
    const prompt = instructions?.text || 'Create a script based on this video';
    return aiService.generateScript(prompt, style, region, language, projectDetails);
};

// ==================== CHARACTER GENERATION ====================

export const generateCharacters = async (script) => {
    return aiService.generateCharacters(script);
};

// ==================== STORYBOARD GENERATION ====================

export const generateStoryboard = async (project, onProgress = null) => {
    return aiService.generateStoryboard(project, onProgress);
};

export const mapEntitiesToShot = (shotData, characters) => {
    const text = (shotData.description + (shotData.dialogue ? JSON.stringify(shotData.dialogue) : '')).toLowerCase();
    const characterIds = characters.filter(c => c.category !== 'element' && text.includes(c.name.toLowerCase())).map(c => c.id);
    const elementIds = characters.filter(c => c.category === 'element' && text.includes(c.name.toLowerCase())).map(c => c.id);
    return { characterIds, elementIds };
};

// ==================== IMAGE GENERATION ====================

export const generateCharacterPortraitWithGemini = async (description, refImages, costumeImages, makeup, aspectRatio, category, modelId) => {
    const result = await aiService.generateCharacterPortrait(description, refImages, costumeImages, makeup, aspectRatio, category, modelId);
    // Return the image, converted to base64 to avoid CORS issues with CDN URLs
    if (result.images && result.images.length > 0) {
        const image = await convertToBase64IfNeeded(result.images[0]);
        return {
            image,
            creditsUsed: result.creditsUsed || 0,
            creditsRemaining: result.creditsRemaining,
            jobId: result.jobId
        };
    }
    throw new Error('No image generated');
};

export const generateSceneImage = async (prompt, aspectRatio, characters = [], referenceImages = [], modelId = 'default') => {
    const result = await aiService.generateSceneImage(prompt, aspectRatio, characters, referenceImages, modelId);
    console.log('🎨 aiService.generateSceneImage result:', {
        hasImages: !!(result.images && result.images.length > 0),
        creditsUsed: result.creditsUsed,
        creditsRemaining: result.creditsRemaining,
        jobId: result.jobId
    });
    // Return the image, converted to base64 to avoid CORS issues with CDN URLs
    if (result.images && result.images.length > 0) {
        const image = await convertToBase64IfNeeded(result.images[0]);
        return {
            image,
            creditsUsed: result.creditsUsed || 0,
            creditsRemaining: result.creditsRemaining,
            jobId: result.jobId
        };
    }
    throw new Error('No image generated');
};

export const editImage = async (baseImage, prompt, characters = [], referenceImages = [], maskImage = null, modelId = 'default', aspectRatio = null, onProgress = null) => {
    const result = await aiService.editImage(baseImage, prompt, characters, referenceImages, maskImage, modelId, aspectRatio, onProgress);
    // Convert to base64 to avoid CORS issues
    const imageUrl = await convertToBase64IfNeeded(result.imageUrl);
    return {
        imageUrl,
        creditsUsed: result.creditsUsed || 0,
        creditsRemaining: result.creditsRemaining,
        jobId: result.jobId
    };
};

// ==================== VIDEO GENERATION ====================

export const generateVideoWithWanAnimate = async (imageUrl, referenceVideoData, onProgress, resolution = '480p') => {
    const result = await aiService.generateVideoWithWan(
        "Animate this image with natural, fluid motion",
        imageUrl,
        '16:9',
        resolution,
        onProgress
    );
    return result.videoUrl;
};

export const generateSceneVideoWithVeoFast = async (prompt, onProgress, aspectRatio, startFrame, endFrame) => {
    // Veo is not available in the new architecture, use default video generation
    const result = await aiService.generateVideo(prompt, 5, 'cinematic', aspectRatio, startFrame, endFrame, onProgress);
    return result.videoUrl;
};

export const generateSceneVideoWithSeedance = async (prompt, onProgress, aspectRatio, startFrame, endFrame) => {
    const result = await aiService.generateVideoWithMinimax(prompt, startFrame, onProgress);
    return result.videoUrl;
};

// ==================== PROMPT GENERATION ====================

export const generateFramePromptsFromDescription = async (description, context = {}) => {
    return aiService.generateFramePrompts(description, context);
};

export const generateVideoPrompt = async (shot, characters, script, duration) => {
    return aiService.generateVideoPrompt(shot, characters, script, duration);
};

// ==================== TRANSCRIPTION ====================

export const transcribeDialogueAudio = async (audioData) => {
    return aiService.transcribeAudio(audioData);
};

export const transcribeShotNarration = transcribeDialogueAudio;

// ==================== UTILITY FUNCTIONS ====================

export const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

export const cropImageToAspectRatio = async (base64Str, aspectRatio) => {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const [wRatio, hRatio] = aspectRatio.split(':').map(Number);
            const targetRatio = wRatio / hRatio;
            const imgRatio = img.width / img.height;
            let renderWidth = img.width;
            let renderHeight = img.height;
            let offsetX = 0;
            let offsetY = 0;

            if (imgRatio > targetRatio) {
                renderWidth = img.height * targetRatio;
                offsetX = (img.width - renderWidth) / 2;
            } else {
                renderHeight = img.width / targetRatio;
                offsetY = (img.height - renderHeight) / 2;
            }
            canvas.width = renderWidth;
            canvas.height = renderHeight;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, offsetX, offsetY, renderWidth, renderHeight, 0, 0, renderWidth, renderHeight);
            resolve(canvas.toDataURL('image/jpeg', 0.9));
        };
        img.onerror = () => resolve(base64Str);
        img.src = base64Str;
    });
};

export const reframeImage = async (img, ratio) => cropImageToAspectRatio(img, ratio);

export const preloadProjectAssets = async (project, onProgress) => {
    // In the new architecture, assets are managed server-side
    // This function now just validates that assets exist
    const assetsToLoad = new Set();
    project.characters?.forEach(c => {
        if (c.image) assetsToLoad.add(c.image);
    });
    project.scenes?.forEach(s => s.shots?.forEach(shot => {
        if (shot.startFrameImage) assetsToLoad.add(shot.startFrameImage);
        if (shot.endFrameImage) assetsToLoad.add(shot.endFrameImage);
    }));
    
    const total = assetsToLoad.size;
    if (total === 0) return;
    if (onProgress) onProgress(`Found ${total} assets to preload`);
    
    // Assets are now URL-based and served from the backend
    // No need to upload to Gemini Files API
    if (onProgress) onProgress(`Assets ready (${total} items)`);
};

// Portrait generation aliases
export const generateCharacterPortraitWithFal = async (prompt, ratio) => {
    return generateCharacterPortraitWithGemini(prompt, [], [], '', ratio, 'character', 'default');
};

export const generateCharacterPortraitWithFalRef = async (prompt, refs, costume, makeup, ratio) => {
    return generateCharacterPortraitWithGemini(prompt, refs, costume, makeup, ratio, 'character', 'default');
};

// Video resize utility (client-side)
export const resizeVideo = async (videoUrl, maxDimension = 1920) => {
    // Return as-is for URLs, the server handles video processing
    if (videoUrl.startsWith('http')) {
        return videoUrl;
    }
    
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.src = videoUrl;
        video.muted = true;
        
        const timeout = setTimeout(() => {
             console.warn("Resize video timeout, returning original.");
             resolve(videoUrl);
        }, 15000);

        video.onloadedmetadata = () => {
            clearTimeout(timeout);
            const { videoWidth, videoHeight } = video;
            
            if (videoWidth <= maxDimension && videoHeight <= maxDimension) {
                resolve(videoUrl);
                return;
            }
            
            // For large videos, just return the URL - server will handle processing
            resolve(videoUrl);
        };
        
        video.onerror = () => {
            clearTimeout(timeout);
            resolve(videoUrl);
        };
    });
};
